import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Warning } from "@phosphor-icons/react";

export default function Audit() {
    const { t } = useI18n();
    const [data, setData] = useState(null);
    const [signinOnly, setSigninOnly] = useState(false);
    const [printOnly, setPrintOnly] = useState(false);
    const load = () => api.get(`/audit`, { params: signinOnly ? { signin_only: true } : printOnly ? { print_only: true } : {} }).then((r) => setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [signinOnly, printOnly]);
    return (
        <div className="space-y-6" data-testid="audit-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· TAMPER-EVIDENT LOG</div>
                    <h1 className="text-3xl font-bold tracking-tight mt-1">{t("audit")}</h1>
                </div>
                {data && (
                    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-widest ${data.chain_intact ? "bg-green-100 text-green-900 border border-green-400" : "bg-primary text-primary-foreground"}`} data-testid="chain-status">
                        {data.chain_intact ? <ShieldCheck size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                        {data.chain_intact ? t("chainIntact") : t("chainBroken")}
                    </div>
                )}
            </div>
            <div className="inline-flex rounded-sm border border-border bg-secondary/40 p-1" data-testid="audit-tabs">
                <button onClick={() => { setSigninOnly(false); setPrintOnly(false); }} className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-sm ${!signinOnly && !printOnly ? "bg-background shadow-sm" : "text-muted-foreground"}`} data-testid="tab-all">{t("auditAll")}</button>
                <button onClick={() => { setSigninOnly(true); setPrintOnly(false); }} className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-sm ${signinOnly ? "bg-background shadow-sm" : "text-muted-foreground"}`} data-testid="tab-signin">{t("auditSigninOnly")}</button>
                <button onClick={() => { setSigninOnly(false); setPrintOnly(true); }} className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-sm ${printOnly ? "bg-background shadow-sm" : "text-muted-foreground"}`} data-testid="tab-print">{t("auditPrintOnly")}</button>
            </div>
            <Card className="rounded-sm border-border overflow-x-auto">
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr>
                            <th className="text-start">{t("auditTimestamp")}</th>
                            <th className="text-start">{t("auditActor")}</th>
                            <th className="text-start">{t("auditAction")}</th>
                            <th className="text-start">{t("auditDeviceIp")}</th>
                            <th className="text-start">{t("auditEntity")}</th>
                            <th className="text-start">{t("auditHash")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.entries?.map((e) => (
                            <tr key={e.id} className="border-t border-border align-top" data-testid={`audit-row-${e.id}`}>
                                <td className="mono text-xs">{new Date(e.ts).toLocaleString()}</td>
                                <td>{e.actor_email || "—"}<div className="text-[10px] uppercase tracking-widest text-muted-foreground">{e.actor_role}</div></td>
                                <td className="mono text-xs">
                                    {e.action}
                                    {e.meta?.suspicious && (
                                        <span className="ms-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary text-primary-foreground text-[10px] uppercase tracking-widest" data-testid={`audit-suspicious-${e.id}`}>
                                            <Warning size={10} weight="fill" />SUSPICIOUS
                                        </span>
                                    )}
                                    {e.meta?.new_ip && !e.meta?.suspicious && (
                                        <span className="ms-2 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-400 text-[10px] uppercase tracking-widest">{t("auditNewIp")}</span>
                                    )}
                                </td>
                                <td className="text-xs mono">
                                    {e.meta?.browser && <div>{e.meta.browser} · {e.meta.device}</div>}
                                    {e.meta?.ip && <div className="text-muted-foreground">{e.meta.ip}</div>}
                                    {e.meta?.country && <div className="text-muted-foreground">{e.meta.country}{e.meta?.country_change && e.meta?.prev_country ? ` ← ${e.meta.prev_country}` : ""}</div>}
                                </td>
                                <td className="mono text-xs">{e.entity}:{e.entity_id?.slice(0, 8)}</td>
                                <td className="mono text-[10px] text-muted-foreground max-w-[240px] truncate">{e.hash}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
        </div>
    );
}
